import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Zap, Users, TrendingUp, Mail } from 'lucide-react';

export default function AboutPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#00FF41] transition-colors mb-8">
          <ArrowLeft className="h-4 w-4" /> Back to Dashboard
        </button>

        <h1 className="text-4xl font-bold mb-2">
          <span className="text-white">About </span>
          <span className="text-[#00FF41]">BitBot</span>
        </h1>
        <p className="text-gray-400 mb-8">Automated cryptocurrency trading, built for everyone.</p>

        <div className="space-y-8">
          <section>
            <h2 className="text-xl font-bold text-[#00FF41] mb-3">What is BitBot?</h2>
            <p className="text-gray-300 leading-relaxed">
              BitBot is an automated cryptocurrency trading platform that uses technical indicators like RSI (Relative Strength Index) to execute buy and sell decisions across multiple coins. It connects directly to your Coinbase account and trades on your behalf, 24/7, based on configurable strategies.
            </p>
            <p className="text-gray-300 leading-relaxed mt-3">
              Whether you're a seasoned trader or just getting started, BitBot handles the complexity of timing the market so you don't have to. Start with simulation mode to learn risk-free, then switch to live trading when you're ready.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#00FF41] mb-3">Key Features</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { icon: TrendingUp, title: 'RSI-Based Trading', desc: 'Automatically buys when coins are oversold and sells when overbought, using proven technical analysis.' },
                { icon: Shield, title: 'Protection Sells', desc: 'Stop-loss, take-profit, and trailing stop mechanisms protect your positions from major downturns.' },
                { icon: Users, title: 'Multi-Coin Support', desc: 'Trade BTC, ETH, SOL, LTC, DOGE, BCH, XRP, and SHIB simultaneously with individual pair controls.' },
                { icon: Zap, title: 'Per-Lot Profit Check', desc: 'Each buy lot is evaluated individually with fee-adjusted profit calculations before any sell executes.' },
              ].map((f) => {
                const Icon = f.icon;
                return (
                  <div key={f.title} className="border border-[#003B00]/50 bg-[#050505] p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="h-4 w-4 text-[#00FF41]" />
                      <h3 className="text-sm font-bold text-white">{f.title}</h3>
                    </div>
                    <p className="text-xs text-gray-400 leading-relaxed">{f.desc}</p>
                  </div>
                );
              })}
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#00FF41] mb-3">About the Author</h2>
            <p className="text-gray-300 leading-relaxed">
              BitBot was created by <span className="text-white font-semibold">Tim Thompson</span> — a technology executive, innovator, and disabled U.S. Army veteran. With decades of experience driving digital transformation across enterprises, Tim is passionate about leveraging AI and automation to create practical tools that make people's lives easier.
            </p>
            <p className="text-gray-300 leading-relaxed mt-3">
              Tim served as an infantry soldier in the legendary 7th Infantry Division at Fort Ord, California. Later assigned to the 7th DMMC, he helped design and implement the Army's first field-level logistics system — coded in COBOL on the backs of pizza boxes in 1982. That system evolved over the years to become the backbone of logistics operations for the 2004 Iraq War.
            </p>
            <blockquote className="border-l-2 border-[#00FF41] pl-4 my-6">
              <p className="text-gray-300 italic">"Technology should empower everyone, not just those who can afford it."</p>
              <p className="text-gray-500 text-sm mt-1">— Tim Thompson</p>
            </blockquote>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#00FF41] mb-3">Contact</h2>
            <p className="text-gray-300">
              Questions, feedback, or partnership inquiries:
            </p>
            <a href="mailto:eviltim@gmail.com" className="inline-flex items-center gap-2 mt-2 text-[#00FF41] hover:text-white transition-colors">
              <Mail className="h-4 w-4" /> eviltim@gmail.com
            </a>
          </section>
        </div>

        <footer className="mt-12 pt-6 border-t border-[#003B00]/30 flex flex-wrap gap-4 text-xs text-gray-600">
          <a href="/privacy" className="hover:text-[#00FF41]">Privacy Policy</a>
          <a href="/terms" className="hover:text-[#00FF41]">Terms of Service</a>
          <a href="/faq" className="hover:text-[#00FF41]">FAQ</a>
          <a href="/changelog" className="hover:text-[#00FF41]">Changelog</a>
        </footer>
      </div>
    </div>
  );
}
